import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { PrescriptionHexRow } from 'src/app/medical-history/models/prescription-hex-row';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { HexAdministration, HexServingMode, TreatmentInstruction } from 'src/app/api/models';
import { SpeciesDiagnosisDropdown } from 'src/app/medical-history/models/species-diagnosis-dropdown.model';
import { componentDestroyed, untilComponentDestroyed } from 'src/app/shared/helpers/rxjs.operator';
import { MedicalHistoryHelper } from 'src/app/shared/helpers/medical-history.helper';
import { PrescriptionStatus } from 'src/app/medical-history/enums/prescription-status.enum';
import { PrescriptionType } from 'src/app/medical-history/enums/prescription-type.enum';
import { MatSelectChange } from '@angular/material/select';
import { DateTimeHelper } from '../../helpers/datetime.helper';

@Component({
  selector: 'diana-diagnosis-treatment-table',
  templateUrl: './diagnosis-treatment-table.component.html',
  styleUrls: ['./diagnosis-treatment-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class DiagnosisTreatmentTableComponent implements OnChanges, OnDestroy {
  @Input() treatmentPrescriptions: PrescriptionHexRow[] = [];
  @Input() diagnosis!: FormArray;
  @Input() diagnosisWithTreatmentIndex!: number;
  @Input() diagnosisWithTreatmentControl!: FormGroup;
  @Input() diagnosisList: SpeciesDiagnosisDropdown[] = [];
  @Input() visitAt!: string;
  @Input() servingModes: Array<HexServingMode> | undefined = [];
  @Input() applicationTypes: Array<HexAdministration> = [];
  @Output() typeIsChanged = new EventEmitter<boolean>();
  @Output() changeDate = new EventEmitter<FormControl>();

  public displayedColumns: string[] = ['expandState', 'type', 'medicineDescription', 'totalDosis', 'totalDeliveryQuantity', 'startDateInstructions', 'endDateInstructions', 'diagnosis', 'isIsabv', 'isTaps'];
  expandedElement: any;
  public PrescriptionStatus = PrescriptionStatus;
  public PrescriptionType = PrescriptionType;
  public prescriptionTypeDropdownList: Array<PrescriptionType> = Object.values(PrescriptionType);
  constructor(private cdr: ChangeDetectorRef) { }

  ngOnChanges(): void {
    // Removing diagnosis column when there is only one treatment
    if (this.treatmentPrescriptions && this.treatmentPrescriptions.length === 1) {
      this.displayedColumns = this.displayedColumns.filter(columns => columns !== 'diagnosis');
    }
    // Checking if exist treatments and generates the object for the treatment table
    if (this.treatmentPrescriptions && this.treatmentPrescriptions.length > 0) {
      this.updateTreatmentsPosition();
      // to disable dosis component when isTaps and isIsabv are false on checkbox value change
      this.diagnosisWithTreatmentControl.get('treatmentsList')?.value.map((treatment: PrescriptionHexRow) => {
        treatment.startDateInstructions = new FormControl({ value: treatment.startDateInstructions.value, disabled: treatment.startDateInstructions.disabled });
        treatment.endDateInstructions = new FormControl({ value: treatment.endDateInstructions.value, disabled: treatment.endDateInstructions.disabled });
        const isIsabvCheckBoxFormControl: FormControl = (treatment.isIsabv as FormControl);
        const isTapsCheckBoxFormControl: FormControl = (treatment.isTaps as FormControl);
        treatment.startDateInstructions.valueChanges.pipe(untilComponentDestroyed(this))
          .subscribe(startDate => {
            if (DateTimeHelper.compareAsc(startDate, treatment.endDateInstructions.value) === 1) {
              treatment.endDateInstructions.setValue(startDate);
            }
          });
        treatment.endDateInstructions.valueChanges.pipe(untilComponentDestroyed(this))
          .subscribe(endDate => {
            if (DateTimeHelper.compareAsc(endDate, treatment.startDateInstructions.value) === -1) {
              treatment.startDateInstructions.setValue(endDate);
            }
          });
        isIsabvCheckBoxFormControl.valueChanges.pipe(untilComponentDestroyed(this))
          .subscribe(isIsabv => {
            if (isIsabv === false && (isTapsCheckBoxFormControl.value === false || isTapsCheckBoxFormControl.disabled)) {
              (treatment.dosagesFormGroup as FormGroup).disable();
              (treatment.deliveryOnStockDosageFormGroup as FormGroup).disable();
            } else if (this.treatmentPrescriptions.length === 1 && this.isTreamentDosageEditable(treatment)) {
              (treatment.dosagesFormGroup as FormGroup).disable();
              (treatment.deliveryOnStockDosageFormGroup as FormGroup).disable();
            } else {
              this.enableTreatmentDosages(treatment);
            }
          })
        isTapsCheckBoxFormControl.valueChanges.pipe(untilComponentDestroyed(this)).subscribe(isTaps => {
          if (isTaps === false && (isIsabvCheckBoxFormControl.value === false || isIsabvCheckBoxFormControl.disabled)) {
            (treatment.dosagesFormGroup as FormGroup).disable();
            (treatment.deliveryOnStockDosageFormGroup as FormGroup).disable();
          } else if (this.treatmentPrescriptions.length === 1 && this.isTreamentDosageEditable(treatment)) {
            (treatment.dosagesFormGroup as FormGroup).disable();
            (treatment.deliveryOnStockDosageFormGroup as FormGroup).disable();
          } else {
            this.enableTreatmentDosages(treatment);
          }
        })
      })
    }
  }
  /**
   * Handles dropdown type change
   *
   * @param event result of the dropdown change
   */
  public typeChange(event: MatSelectChange, tableElement: PrescriptionHexRow): void {
    // Enabling or disabling deliveryOnStockDosageFormGroup/dosagesFormGroup to prevent them from been considered on the form validation
    switch (event.value) {
      case PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT: {
        tableElement.deliveryOnStockDosageFormGroup?.disable();
        tableElement.dosagesFormGroup?.enable();
        tableElement.totalDeliveryQuantity = tableElement.treatmentQuantity;
        break;
      }
      case PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT: {
        tableElement.dosagesFormGroup?.disable();
        tableElement.deliveryOnStockDosageFormGroup?.enable();
        tableElement.deliveryOnStockDosageFormGroup?.get('units')?.disable();
        break;
      }
    }
    this.typeIsChanged.emit();
  }

  private isTreamentDosageEditable(treatment: PrescriptionHexRow): boolean {
    return (treatment.isabvState === PrescriptionStatus.SENT || treatment.tapsState === PrescriptionStatus.SENT ||
      treatment.isabvState === PrescriptionStatus.RESENT || treatment.tapsState === PrescriptionStatus.RESENT ||
      treatment.isabvState === PrescriptionStatus.CANCELED || treatment.tapsState === PrescriptionStatus.CANCELED);
  }

  private enableTreatmentDosages(treatment: PrescriptionHexRow): void {
    switch (treatment.type.value) {
      case (PrescriptionType.SINGLE_FARM_ANIMAL_TREATMENT): {
        if (treatment.dosagesFormGroup) {
          treatment.dosagesFormGroup.enable();
          treatment.deliveryOnStockDosageFormGroup?.disable();
          // To disable again the units because enable() with set it ready for user changes
          (treatment?.dosagesFormGroup?.get('dosages') as FormArray).controls.map(dosage => {
            dosage.get('units')?.disable();
          });
        }
        break;
      }
      case (PrescriptionType.SUPPLY_FARM_ANIMAL_TREATMENT): {
        if (treatment.deliveryOnStockDosageFormGroup) {
          treatment.deliveryOnStockDosageFormGroup.enable();
          treatment.dosagesFormGroup?.disable();
          // To disable again the units because enable() with set it ready for user changes
          treatment.deliveryOnStockDosageFormGroup?.get('units')?.disable();
        }
        break;
      }
    }

  }

  public showCalendarPage(date: FormControl): void {
    if (date.enabled) {
      this.changeDate.emit(date)
    }
  }

  ngOnDestroy(): void {
    componentDestroyed(this);
  }

  /**
 * Switchs the treatment position between ddiagnosis
 * @param treatment treatments in the table
 */
  updateTreatmentsPosition(): void {
    this.diagnosis.controls.map((control, indexDiagnosis) => {
      let diagnosisTreatments: PrescriptionHexRow[] = [];
      this.treatmentPrescriptions.map(treatmentData => {
        if (treatmentData.diagnosisId && control.get('diagnosisLevel2')?.value && treatmentData.diagnosisId === control.get('diagnosisLevel2')?.value) {
          diagnosisTreatments.push(treatmentData);
        }
        if (indexDiagnosis === 0 && !treatmentData.diagnosisId) {
          diagnosisTreatments.push(treatmentData);
        }
      });
      control.get('treatmentsList')?.setValue(diagnosisTreatments);
      control.get('treatmentsList')?.value.map((treatment: PrescriptionHexRow) => {
        if (treatment?.isIsabv?.value === false && treatment?.isTaps?.value === false) {
          (treatment.dosagesFormGroup as FormGroup).disable();
        }
      });

      control.get('treatmentsList')?.updateValueAndValidity();
    });
  }

  public setEndDateIntructions(prescription: PrescriptionHexRow, event: Date): void {
    prescription.endDateInstructions.setValue(event);
  }

  handleChangingTreatmentDiagnosis(event: MouseEvent, treatment: PrescriptionHexRow, diagnosisId: string, diagnosis: any): void {
    event.stopPropagation();
    event.preventDefault();
    treatment.diagnosisId = diagnosisId;
    // update with current changes in the dosis
    treatment.treatmentInstruction = this.convertGroupDosageToTreatmentInstruction(treatment);
    // changes position of the treatment based on the diagnosisId inside of the treatments
    this.updateTreatmentsPosition();
    this.diagnosis.markAsDirty();
  }

  public toggleRow(row: { expanded: boolean; }) {
    row.expanded = !row.expanded
  }

  public setHexTotalDosis(totalDosis: number, treatment: PrescriptionHexRow): void {
    treatment.hexTotalDosis = MedicalHistoryHelper.convertHexQuantity(
      totalDosis,
      treatment.salesArticleId || '',
      treatment.articleId,
      treatment.serviceArticleTreatmentsByArticleId
    );
    this.cdr.detectChanges();
  }

  public setDosis(hexTotalDosis: number, treatment: PrescriptionHexRow): void {
    treatment.totalDosis = ((treatment.totalDeliveryQuantity || 0) * hexTotalDosis) / (treatment.hexTotalDeliveryQuantity);
  }

  public getDiangnosisLevelText(diagnosisId: string) {
    const diagnosis = this.diagnosisList.find(d => d.id == diagnosisId);
    return diagnosis?.codeLevel1 + '.' + diagnosis?.codeLevel2;
  }
  /**
   * On changing tratment to another diagnosis the dosis component created new and uses TreatmentInstruction values not what is in the moment on dosagesFormGroup
   * This function generates and update TreatmentInstruction on the treatment with the current changes
   *
   * @param treatment treatment to change position
   * @returns utreatmentInstruction with the current values
   */
  private convertGroupDosageToTreatmentInstruction(treatment: PrescriptionHexRow): TreatmentInstruction | undefined {
    let treatmentInstructions = treatment.treatmentInstruction || {};
    if (treatment) {
      const dosages = treatment.dosagesFormGroup?.get('dosages') as FormArray;
      if (dosages.controls.length > 0) {
        treatmentInstructions.firstDosage = this.getNumber(dosages.controls[0].get('amount')?.value);
        treatmentInstructions.firstDuration = this.getNumber(dosages.controls[0].get('durationInDays')?.value);
        treatmentInstructions.firstServingModeId = dosages.controls[0].get('servingMode')?.value.id;
        treatmentInstructions.administrationId = dosages.controls[0].get('applicationType')?.value.id;
      };
      if (dosages.controls.length > 1) {
        treatmentInstructions.secondDosage = this.getNumber(dosages.controls[1].get('amount')?.value);
        treatmentInstructions.secondDuration = this.getNumber(dosages.controls[1].get('durationInDays')?.value);
        treatmentInstructions.secondServingModeId = dosages.controls[1].get('servingMode')?.value.name;
      }
    }

    return treatmentInstructions;
  }

  private getNumber(value: string): any {
    return !isNaN(parseFloat(value || '')) ? Number(value) : null
  }

}
